import React from 'react';
import { Box, Chip, CircularProgress, Avatar } from '@mui/material';
import { PluginsPillState } from 'types/Chats/ChatMessage';

export type PluginsPillProps = {
  pluginsPill: PluginsPillState;
};

const PluginsPill: React.FC<PluginsPillProps> = ({ pluginsPill }) => {
  if (!pluginsPill?.isVisible || !pluginsPill?.functions?.length) {
    return null;
  }

  const renderStatusIndicator = (status: string, statusIconURL?: string | null) => {
    switch (status) {
      case 'arguments_started':
        return (
          <Box
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              backgroundColor: 'white',
              borderRadius: '50%',
              width: 12,
              height: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={8} thickness={6} />
          </Box>
        );
      case 'pending':
        return statusIconURL ? (
          <Box
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              backgroundColor: 'white',
              borderRadius: '50%',
              width: 12,
              height: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={statusIconURL}
              alt="pending"
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              backgroundColor: 'var(--color-warning-main, #ff9800)',
              borderRadius: '50%',
              width: 8,
              height: 8,
              border: '2px solid white',
            }}
          />
        );
      case 'completed':
      case 'error':
        return statusIconURL ? (
          <Box
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              backgroundColor: 'white',
              borderRadius: '50%',
              width: 12,
              height: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={statusIconURL}
              alt="status"
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
              }}
            />
          </Box>
        ) : null;
      default:
        return null;
    }
  };

  const renderFunctionIcon = (func: any, index: number) => (
    <Box
      key={func.callId}
      sx={{
        position: 'relative',
        marginLeft: index > 0 ? '-4px' : 0,
        zIndex: pluginsPill.functions.length - index,
      }}
    >
      <Avatar
        sx={{
          width: 20,
          height: 20,
          backgroundColor: 'white',
          border: '1px solid var(--color-border)',
        }}
        src={func.iconURL || undefined}
      >
        {/* Fallback for missing icon */}
        <Box
          sx={{
            width: 12,
            height: 12,
            backgroundColor: 'var(--color-primary)',
            borderRadius: '2px',
          }}
        />
      </Avatar>
      {renderStatusIndicator(func.status, func.eventStatusTypeIconURL)}
    </Box>
  );

  return (
    <Box sx={{ marginBottom: 0.5 }}>
      <Chip
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {pluginsPill.functions.map((func, index) =>
                renderFunctionIcon(func, index)
              )}
            </Box>
            <Box
              sx={{
                fontSize: '16px',
                fontWeight: 400,
                color: 'var(--color-text-secondary, rgba(0, 0, 0, 0.6))',
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent 20%, rgba(255,255,255,0.7) 50%, transparent 80%)',
                  animation: 'shimmer 2s infinite ease-in-out',
                },
                '@keyframes shimmer': {
                  '0%': {
                    left: '-100%',
                  },
                  '100%': {
                    left: '100%',
                  },
                },
              }}
            >
              Calling Plugins
            </Box>
          </Box>
        }
        size="small"
        sx={{
          backgroundColor: 'var(--color-grey-50)',
          borderRadius: '16px',
          height: '32px',
          '& .MuiChip-label': {
            padding: '0 12px 0 4px',
          },
        }}
      />
    </Box>
  );
};

export default PluginsPill; 